import { useAtomValue } from "jotai";
import { useFlags } from "@/utils/flagsmith/useFlags";
import { DetailTableRow } from "../../components/DetailTable";
import { useColorizedValue } from "../useColorizedValue";
import { balanceSheetMetricsAtom } from "../../metrics.state";
import {
    FinanceLabField,
    FinanceLabFieldType,
    formatFinanceLabValue,
} from "@/entities/FinanceLab";
import { getLastValue } from "@/utils/metrics/metricsUtils";
import { AccountLabel } from "@/entities/Labeling/Account.model";
import { COMING_SOON } from "@/entities/CreditBox/CreditBox.types";
import { getMetric } from "../../utils/getMetric";
import { scrollAndHighlightRow } from "../../utils/scrollAndHighlight";
import { useMetricClickHandler } from "../shared/useMetricClickHandler";

export function useBalanceSheetData(): DetailTableRow[] {
    const flags = useFlags();
    const showComingSoonFields = flags.enabled("show-coming-soon-placeholders");
    const showM05 = flags.enabled("show-im-m05");
    const handleMetricClick = useMetricClickHandler();
    const balanceSheetMetrics = useAtomValue(balanceSheetMetricsAtom);

    const cash = getMetric(
        balanceSheetMetrics,
        AccountLabel.CURRENT_ASSETS_CASH_AND_CASH_EQUIVALENTS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_CASH,
    );
    const totalAssets = getMetric(
        balanceSheetMetrics,
        AccountLabel.ASSETS,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_ASSETS,
    );
    const totalLiabilities = getMetric(
        balanceSheetMetrics,
        AccountLabel.LIABILITIES,
        FinanceLabField.ORGANIZATION_FINANCIAL_DATA_TOTAL_LIABILITIES,
    );

    const assetsColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(totalAssets?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        totalAssets?.source,
        totalAssets?.info.derived.label,
    );
    const liabilitiesColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(totalLiabilities?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        totalLiabilities?.source,
        totalLiabilities?.info.derived.label,
    );

    const cashBalanceColorized = useColorizedValue(
        formatFinanceLabValue(
            getLastValue(cash?.values)?.value,
            FinanceLabFieldType.CURRENCY,
        ) || "-",
        cash?.source,
        cash?.info.derived.label,
    );

    return [
        {
            label: "Total Assets",
            value: assetsColorized.value,
            valueClassName: assetsColorized.className,
            source: assetsColorized.source,
            sourceClassName: assetsColorized.sourceClassName,
            delta: totalAssets?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Total Assets",
                      )
                    : handleMetricClick(totalAssets, "Total Assets"),
        },
        ...(showComingSoonFields
            ? [
                  {
                      label: "Gross PP&E",
                      value: COMING_SOON,
                  },
              ]
            : []),
        {
            label: "Cash & Equivalents",
            value: cashBalanceColorized.value,
            valueClassName: cashBalanceColorized.className,
            source: cashBalanceColorized.source,
            sourceClassName: cashBalanceColorized.sourceClassName,
            delta: cash?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Cash & Equivalents",
                      )
                    : handleMetricClick(cash, "Cash & Equivalents"),
        },
        {
            label: "Total Liabilities",
            value: liabilitiesColorized.value,
            valueClassName: liabilitiesColorized.className,
            source: liabilitiesColorized.source,
            sourceClassName: liabilitiesColorized.sourceClassName,
            delta: totalLiabilities?.delta,
            onClick: () =>
                showM05
                    ? scrollAndHighlightRow(
                          "summary-balance-sheet",
                          "Total Liabilities",
                      )
                    : handleMetricClick(totalLiabilities, "Total Liabilities"),
        },
        ...(showComingSoonFields
            ? [
                  {
                      label: "Accounts Receivable",
                      value: COMING_SOON,
                  },
                  {
                      label: "Accounts Payable",
                      value: COMING_SOON,
                  },
                  {
                      label: "Inventory",
                      value: COMING_SOON,
                  },
                  {
                      label: "Current Debt Balance",
                      value: COMING_SOON,
                  },
              ]
            : []),
    ];
}
